import React, { useState, useRef } from "react";
import { uploadFile } from "../../apis"; // adjust import as needed
import { toast } from "react-toastify";
import { IoMdClose } from "react-icons/io";
import "./outfitEditor.css";

function SimpleOutfitEditor({ user, image, wardrob, onOutfitCreated }) {
  // Array of images that will be combined.
  // Each image object may include an optional "wardrobeId" (if selected from wardrobe).
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  const dragRef = useRef({ id: null, offsetX: 0, offsetY: 0 });
  // Show modal for selecting from wardrobe
  const [showWardrobeModal, setShowWardrobeModal] = useState(false);

  // Handle adding image either from file input or from wardrobe selection.
  const handleFileChange = (eOrUrl, wardrobeId, type) => {
    let newImageUrl;
    if (type === "file") {
      const file = eOrUrl.target.files[0];
      if (!file) return;
      newImageUrl = URL.createObjectURL(file);
    } else if (type === "wardrobe") {
      newImageUrl = eOrUrl; // here eOrUrl is the URL string from the wardrobe item
    }

    const img = new Image();
    img.onload = () => {
      const aspectRatio = img.width / img.height;
      const newImage = {
        id: Date.now().toString(),
        url: newImageUrl,
        x: 0,
        y: 0,
        width: 100,
        height: 100 / aspectRatio,
        rotation: 0,
        zIndex: images.length,
        // If selected from wardrobe, store its id; if file upload, leave undefined.
        wardrobeId: wardrobeId || null,
      };
      setImages((prev) => [...prev, newImage]);
    };
    img.src = newImageUrl;
  };

  // Basic drag-and-drop functionality
  const handleMouseDown = (e, id) => {
    const img = images.find((i) => i.id === id);
    if (!img) return;
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      id,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    bringToFront(id);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!dragRef.current.id) return;
    const editorRect = editorRef.current.getBoundingClientRect();
    const img = images.find((i) => i.id === dragRef.current.id);
    let newX = e.clientX - editorRect.left - dragRef.current.offsetX;
    let newY = e.clientY - editorRect.top - dragRef.current.offsetY;
    // Constrain within editor
    newX = Math.max(0, Math.min(newX, editorRect.width - img.width));
    newY = Math.max(0, Math.min(newY, editorRect.height - img.height));
    setImages((prev) =>
      prev.map((i) =>
        i.id === dragRef.current.id ? { ...i, x: newX, y: newY } : i
      )
    );
  };

  const handleMouseUp = () => {
    dragRef.current = { id: null, offsetX: 0, offsetY: 0 };
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  };

  // Bring the selected image to the front by increasing its zIndex.
  const bringToFront = (id) => {
    setImages((prev) => {
      const maxZIndex = Math.max(...prev.map((i) => i.zIndex));
      return prev.map((i) =>
        i.id === id ? { ...i, zIndex: maxZIndex + 1 } : i
      );
    });
  };

  // Combine all images on a canvas and upload the result.
  const saveImage = async () => {
    if (images.length === 0) {
      toast.error("No images to combine");
      return;
    }
    const editorRect = editorRef.current.getBoundingClientRect();
    const canvas = document.createElement("canvas");
    canvas.width = editorRect.width;
    canvas.height = editorRect.height;
    const ctx = canvas.getContext("2d");

    // Sort images by zIndex for proper layering.
    const sortedImages = [...images].sort((a, b) => a.zIndex - b.zIndex);
    const loadedImages = await Promise.all(
      sortedImages.map(
        (img) =>
          new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "Anonymous";
            image.onload = () => resolve(image);
            image.onerror = ()=>{
              console.log('error in loading image');
              return reject;
            };
            image.src = img.url;
          })
      )
    );

    loadedImages.forEach((img, index) => {
      const imgData = sortedImages[index];
      ctx.save();
      ctx.translate(
        imgData.x + imgData.width / 2,
        imgData.y + imgData.height / 2
      );
      ctx.rotate((imgData.rotation * Math.PI) / 180);
      ctx.drawImage(
        img,
        -imgData.width / 2,
        -imgData.height / 2,
        imgData.width,
        imgData.height
      );
      ctx.restore();
    });

    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error("Error generating image");
        return;
      }
      const file = new File([blob], "combined_image.png", { type: "image/png" });
      const formData = new FormData();
      formData.append("file", file);
      try {
        const uploadResponse = await uploadFile(formData);
        if (uploadResponse && uploadResponse.data) {
          // Get wardrobe IDs from images that were selected from wardrobe.
          const wardrobeImageIds = images
            .filter((img) => img.wardrobeId)
            .map((img) => img.wardrobeId);
          const outfitData = {
            combinedImageUrl: uploadResponse.data.url,
            wardrobeImageIds,
          };
          onOutfitCreated && onOutfitCreated(outfitData);
          toast.success("Outfit created successfully");
        } else {
          toast.error("Upload failed");
        }
      } catch (error) {
        toast.error("Error uploading image");
      }
    }, "image/png");
  };

  return (
    <div className="simple-outfit-editor">
      <div className="header">
        {!image && <h2>Create Outfit</h2>}

        {/* {JSON.stringify(wardrob)} */}
      </div>
      <div className="controls">
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => handleFileChange(e, null, "file")}
        />
        {!image &&
          <div>
            <button onClick={() => setShowWardrobeModal(true)}>
              {user?.username} Wardrobe
            </button></div>
        }
      </div>
      <div className="editor" ref={editorRef}>
        {image ? <img className="wardrobe-item" style={{ height: '100%', width: '100%' }} src={image} /> : null}
        {images.map((img) => (
          <div
            key={img.id}
            className="image-container"
            style={{
              left: `${img.x}px`,
              top: `${img.y}px`,
              width: `${img.width}px`,
              height: `${img.height}px`,
              transform: `rotate(${img.rotation}deg)`,
              zIndex: img.zIndex,
              border: "2px solid transparent",
            }}
            onMouseDown={(e) => {
              bringToFront(img.id);
              handleMouseDown(e, img.id);
            }}
          >
            <img src={img.url} alt="Selected" className="draggable-image" />
          </div>
        ))}
      </div>
     {!image &&  <button className="save-button" onClick={saveImage}>
        Save and Upload
      </button>}

      {showWardrobeModal && (
        <div className="modal-overlay">
          <div className="modal_content">
            <div className="modal-header">
              <h3>Select Wardrobe Image</h3>
              <button
                className="close-button"
                onClick={() => setShowWardrobeModal(false)}
              >
                <IoMdClose size={24} />
              </button>
            </div>
            <div className="wardrobe-list">

              {wardrob && wardrob.length > 0 ? (
                wardrob.map((item) => (
                  <img
                    key={item._id}
                    src={item.image}
                    alt="Wardrobe item"
                    className="wardrobe-item"
                    onClick={() => {
                      handleFileChange(item.image, item._id, "wardrobe");
                      setShowWardrobeModal(false);
                    }}
                  />
                ))
              ) : (
                <p>No wardrobe items available </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SimpleOutfitEditor;
